import { Fragment, type PropsWithChildren } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ClockIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import LogoMark from '../ui/LogoMark';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { logout } from '../../api/auth';
import { useAuth } from '../../providers/AuthProvider';
import { useNotify } from '../ui/notifications/NotificationProvider';
import useCurrentUser from '../../hooks/useCurrentUser';
import usePlatformAccounts from '../../hooks/usePlatforms';
import { notificationsClient } from '../../realtime/notifications-client';
import { formatUsernameDisplay } from '../../utils/display-name';

const getInitials = (username?: string | null, email?: string | null) => {
  const source = username?.trim() || email?.trim();
  if (!source) {
    return 'EF';
  }
  return source.charAt(0).toUpperCase();
};

const postsNavItems = [
  { to: '/posts/create', label: 'Create', icon: PencilSquareIcon },
  { to: '/posts/calendar', label: 'Calendar', icon: CalendarDaysIcon },
  { to: '/posts', label: 'History', icon: ClockIcon, end: true },
] as const;

const AppShell = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const { data: currentUser } = useCurrentUser();
  const { data: platformData } = usePlatformAccounts({ userId: currentUser?.id });
  const { notifyError } = useNotify();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      notificationsClient.disconnect();
      setSession(null);
      queryClient.clear();
      navigate('/login', { replace: true });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to sign out';
      notifyError('Sign out failed', message);
    },
  });

  const handleSignOut = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('sf-manual-logout', '1');
    }
    mutation.mutate();
  };

  const username = (currentUser?.username as string | undefined)?.trim();
  const userEmail = (currentUser?.email as string | undefined)?.trim();
  const emailPrefix = userEmail ? userEmail.split('@')[0] : '';
  const displayUsername = formatUsernameDisplay(username, emailPrefix, '');
  const isAdmin = currentUser?.role === 'admin';
  const postsActive = location.pathname === '/posts' || location.pathname.startsWith('/posts/');

  const mobileNavItems = [
    { to: '/', label: 'Dashboard' },
    { to: '/analytics', label: 'Analytics' },
    { to: '/coach', label: 'Coaching' },
    ...postsNavItems,
    { to: '/media', label: 'Media' },
    { to: '/faq', label: 'FAQ' },
    { to: '/settings', label: 'Settings' },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
  ] as const;

  return (
    <div className="flex min-h-screen flex-col bg-surface-default text-text-primary transition-colors">
      <header className="border-b border-border-card/5 bg-surface backdrop-blur">
        <div className="mx-auto flex h-20 w-full max-w-6xl items-center justify-between px-4">
          <div className="flex items-center">
            <LogoMark className="h-10 sm:h-12" />
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium text-muted md:flex">
            <NavLink className="transition hover:text-primary" to="/">
              Dashboard
            </NavLink>
            <NavLink className="transition hover:text-primary" to="/analytics">
              Analytics
            </NavLink>
            <NavLink className="transition hover:text-primary" to="/coach">
              Coaching
            </NavLink>
            <Menu as="div" className="relative">
              <Menu.Button
                className={`inline-flex items-center gap-1 transition hover:text-primary ${
                  postsActive ? 'text-primary' : ''
                }`}
              >
                Posts
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 z-20 mt-3 w-44 origin-top-left rounded-md border border-border-strong bg-surface p-1 shadow-[var(--shadow-soft)] focus:outline-none">
                  {postsNavItems.map((item) => {
                    const Icon = item.icon;

                    return (
                      <Menu.Item key={item.to}>
                        {({ active }) => (
                          <NavLink
                            to={item.to}
                            end={'end' in item ? item.end : undefined}
                            className={({ isActive }) =>
                              `flex items-center gap-2 rounded px-3 py-2 text-sm transition ${
                                isActive || active
                                  ? 'bg-surface-secondary text-primary'
                                  : 'text-muted hover:text-primary'
                              }`
                            }
                          >
                            <Icon className="h-4 w-4" aria-hidden="true" />
                            {item.label}
                          </NavLink>
                        )}
                      </Menu.Item>
                    );
                  })}
                </Menu.Items>
              </Transition>
            </Menu>
            <NavLink className="transition hover:text-primary" to="/media">
              Media
            </NavLink>
            <NavLink className="transition hover:text-primary" to="/faq">
              FAQ
            </NavLink>
            <NavLink className="transition hover:text-primary" to="/settings">
              Settings
              {platformData?.limits ? (
                <span className="ml-2 rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  {platformData.limits.currentCount}/{platformData.limits.availablePlatforms.length}
                </span>
              ) : null}
            </NavLink>
            {isAdmin ? (
              <NavLink className="transition hover:text-primary" to="/admin">
                Admin
              </NavLink>
            ) : null}
          </nav>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              className="px-3 py-2 md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Bars3Icon className="h-5 w-5" />
            </Button>
            <div className="hidden text-right text-xs text-muted sm:block">
              <p className="font-semibold text-primary">
                {displayUsername ? `Welcome, ${displayUsername}` : 'Welcome'}
              </p>
              <p>Manage workspace</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-semibold text-text-primary">
              {getInitials(username, userEmail)}
            </div>
            <Button
              type="button"
              variant="ghost"
              className="text-xs"
              onClick={handleSignOut}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Signing out…' : 'Sign out'}
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-8">
        {children}
      </main>
      <footer className="border-t border-border-card/5 bg-surface-secondary/90">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-6 text-xs text-muted">
          <span>© {new Date().getFullYear()} ViralEngine</span>
          <div className="flex gap-4">
            <span>Status: <span className="text-primary">Operational</span></span>
            <span>Version: 0.1.0</span>
          </div>
        </div>
      </footer>

      <Modal open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} title="Menu" description="Navigate ViralEngine">
        <nav className="space-y-2">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : undefined}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-semibold transition ${
                  isActive
                    ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-bright'
                    : 'border-border-default bg-surface-default/20 text-text-primary hover:bg-surface-secondary'
                }`
              }
            >
              <span>{item.label}</span>
              <span className="text-xs text-text-muted">Go</span>
            </NavLink>
          ))}
        </nav>
        <div className="flex justify-end">
          <Button type="button" variant="secondary" className="inline-flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
            <XMarkIcon className="h-4 w-4" />
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AppShell;
